import { Box } from '@theme-ui/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import { Magento2_ProductStockStatus } from '@__generated__/globalTypes';
import {
  AlertAction,
  AlertLayout,
  AlertMessage,
  AlertTitle,
  AlertType,
} from '@app/components/Alert';
import Alerts from '@app/components/Alerts';
import { Button, getButtonStyles } from '@app/components/Button';
import ErrorBoundary from '@app/components/ErrorBoundary';
import Link from '@app/components/Link';
import Loader from '@app/components/Loader';
import PageTitle from '@app/components/PageTitle';
import { CartAlertsProvider } from '@app/context/CartAlertsContext';
import { useGetCartId } from '@app/context/CartIdProvider/CartIdProvider';
import { isTruthy } from '@app/helpers/predicate';
import { pushPerpetualEcommerceEvent } from '@app/helpers/tracking';
import { useAddProductFromUrlQuery } from '@app/hooks/useAddProductFromUrlQuery';
import type { AlertItem } from '@app/hooks/useAlerts';
import useGTMTracking from '@app/hooks/useGTMTracking';
import { useRemoveCartItem } from '@app/hooks/useRemoveCartItem';
import useSimpleCart from '@app/hooks/useSimpleCart';
import type { CartQuery_magento2_cart_items } from '@app/pages/Cart/__generated__/LegacyCartQueriesTypes';
import type { AppTheme } from '@app/styles/theme';

import { CHECKOUT_LINK, CONTINUE_SHOPPING_LINK } from './Cart.constants';
import messages from './Cart.messages';
import {
  CartGrid,
  CartTotalsCell,
  CheckoutButtonCell,
  ContinueShoppingButtonCell,
} from './Cart.styles';
import CartAlerts from './CartAlerts';
import { AppliedCoupons, CouponForm } from './CartCoupon/CartCoupon';
import CartEmpty from './CartEmpty';
import { AppliedGiftCards, GiftCardForm } from './CartGiftCard/CartGiftCard';
import CartItems from './CartItems/CartItems';
import CartTotals from './CartTotals/CartTotals';
import ShopStoreAlerts from './ShopStoreAlerts/ShopStoreAlerts';

export const CART_TYPE_STORAGE_KEY = 'shop/cart-type';

const CartContent = () => {
  const { push } = useHistory();
  const { language } = useParams<{ language: string }>();
  const [checkoutAttempts, setCheckoutAttempts] = useState(0);
  const { removeItem, loading: removeItemLoading } = useRemoveCartItem();
  const { loading: isAddingProduct } = useAddProductFromUrlQuery();
  const { cartId } = useGetCartId();

  const {
    loading: cartLoading,
    items,
    prices,
    storeConfig,
    appliedCoupons,
    isVirtual,
  } = useSimpleCart();

  const loading = cartLoading || removeItemLoading || isAddingProduct;

  const outOfStockAlerts = useMemo(
    () =>
      items
        ?.filter(isOutOfStockItem)
        .map(itemToOutOfStockAlert(removeItem, checkoutAttempts > 0)) ?? [],
    [items, removeItem, checkoutAttempts],
  );

  useGTMTracking({ step: 'cart', items, prices, storeConfig, cartId });

  useEffect(() => {
    if (isTruthy(isVirtual)) {
      // We need this in order to show the proper steps on StepProgress component.
      // This way we avoid an extra CustomerCartId query just to retrieve this information,
      // which was causing some issues when merging carts.
      storeCartIsVirtual();
    } else {
      removeStoredCartIsVirtual();
    }
  }, [isVirtual]);

  useEffect(() => {
    if (items?.length) {
      pushPerpetualEcommerceEvent('view_cart', {
        currency: prices?.grandTotal?.currency,
        value: prices?.grandTotal?.value,
        coupons: appliedCoupons,
        cart_id: cartId,
        items: items?.map((item) => ({
          // item_id/item_name should come from the parent product,
          // but it's not available in the order item data yet.
          // @TODO [ECOM-85, ECOM-84]
          item_id: item?.product?.sku,
          item_name: item?.product?.name,
          sku_id: item?.product?.sku,
          sku_name: item?.product?.name,
          sdbs_id: item?.product?.sdbsId,
          price: item?.prices?.price.value || 0,
          quantity: item?.quantity,
        })),
      });
    }
  }, [cartId, items, prices, appliedCoupons]);

  const handleCheckoutClick = useCallback(() => {
    if (items && prices) {
      pushPerpetualEcommerceEvent('begin_checkout', {
        cart_id: cartId,
        currency: prices?.grandTotal?.currency,
        value: prices?.grandTotal?.value,
        coupons: appliedCoupons,
        items: items?.map((item) => ({
          // item_id/item_name should come from the parent product,
          // but it's not available in the cart item data yet.
          // @TODO [ECOM-85, ECOM-84]
          item_id: item?.product?.sku,
          item_name: item?.product?.name,
          sku_id: item?.product?.sku,
          sku_name: item?.product?.name,
          sdbs_id: item?.product?.sdbsId,
          price: item?.prices?.price.value || 0,
          quantity: item?.quantity,
        })),
      });
    }

    if (outOfStockAlerts?.length) {
      setCheckoutAttempts((current) => current + 1);

      return;
    }

    push(generatePath(CHECKOUT_LINK, { language }));
  }, [outOfStockAlerts, push, language, items, prices, cartId, appliedCoupons]);

  if (loading) {
    return <Loader />;
  }

  if (!items?.length) {
    return <CartEmpty />;
  }

  return (
    <>
      <Box mb={16}>
        <ShopStoreAlerts items={items} />
      </Box>

      <Box mb={16}>
        <CartAlerts />
      </Box>

      {Boolean(outOfStockAlerts.length) && (
        <Box mb={16}>
          {/* change key to force re-render, triggering shake animation if set */}
          <Alerts alerts={outOfStockAlerts} key={checkoutAttempts} />
        </Box>
      )}

      <CartGrid>
        <CartItems items={items} storeConfig={storeConfig} cartId={cartId} />
        <AppliedCoupons cartId={cartId} />
        <AppliedGiftCards cartId={cartId} />
        <CouponForm cartId={cartId} />
        <GiftCardForm cartId={cartId} />

        <ContinueShoppingButtonCell hAlign="left">
          <Link
            to={CONTINUE_SHOPPING_LINK}
            data-testid="cart-continue-shopping-button"
            css={(theme: AppTheme) =>
              getButtonStyles({ type: 'secondary', theme })
            }
          >
            <FormattedMessage {...messages.continueShoppingCTA} />
          </Link>
        </ContinueShoppingButtonCell>

        <CartTotalsCell hAlign="right">
          <CartTotals
            cartId={cartId}
            prices={prices}
            storeConfig={storeConfig}
          />
        </CartTotalsCell>

        <CheckoutButtonCell hAlign="right">
          <Button
            type="primary"
            onClick={handleCheckoutClick}
            wide
            data-testid="cart-checkout-button"
          >
            <FormattedMessage {...messages.checkoutCTA} />
          </Button>
        </CheckoutButtonCell>
      </CartGrid>
    </>
  );
};

const Cart = () => (
  <>
    <PageTitle>{messages.title}</PageTitle>
    <Box p={40} bg="white" sx={{ flex: 1 }}>
      <ErrorBoundary>
        <CartAlertsProvider>
          <CartContent />
        </CartAlertsProvider>
      </ErrorBoundary>
    </Box>
  </>
);

export default Cart;

const isOutOfStockItem = (
  item: CartQuery_magento2_cart_items | null,
): item is CartQuery_magento2_cart_items =>
  Boolean(
    item?.product.stockStatus === Magento2_ProductStockStatus.OUT_OF_STOCK,
  );

const itemToOutOfStockAlert =
  (removeItem: (id: string) => Promise<void>, shouldShake: boolean) =>
  ({ uid, product }: CartQuery_magento2_cart_items): AlertItem => ({
    shouldShake,
    type: AlertType.Error,
    layout: AlertLayout.Column,
    content: (
      <>
        <AlertTitle>
          <FormattedMessage
            {...messages.alertOutOfStockTitle}
            values={{ productName: product.name }}
          />
        </AlertTitle>
        <AlertMessage>
          <FormattedMessage {...messages.alertOutOfStockMessage} />
        </AlertMessage>
      </>
    ),
    actions: (
      <AlertAction>
        <button
          onClick={() => removeItem(uid)}
          data-testid={`alert-out-of-stock-remove-${product.sku}`}
        >
          <FormattedMessage {...messages.alertOutOfStockActionRemove} />
        </button>
      </AlertAction>
    ),
  });

export const storeCartIsVirtual = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(CART_TYPE_STORAGE_KEY, 'virtual');
  }
};

export const getStoredCartIsVirtual = () =>
  typeof localStorage !== 'undefined'
    ? localStorage.getItem(CART_TYPE_STORAGE_KEY) === 'virtual'
    : false;

export const removeStoredCartIsVirtual = () =>
  typeof localStorage !== 'undefined'
    ? localStorage.removeItem(CART_TYPE_STORAGE_KEY)
    : null;
